import { createContext, useContext, useEffect, useState, useReducer, useCallback, type ReactNode } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  confirmPasswordReset as fbConfirmPasswordReset,
  sendEmailVerification as fbSendEmailVerification,
  updatePassword as fbUpdatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  type User,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { withAuthError } from '@/lib/authErrors';
import { reportReferralConversion } from '@/lib/referral';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** True when the signed-in user carries the `platformAdmin` custom claim. Cosmetic only — every admin callable re-checks this server-side. */
  isAdmin: boolean;
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  reloadUser: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  reauthenticate: (currentPassword: string) => Promise<void>;
  deleteAccount: (currentPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Where Firebase should send users back to after email actions (verify / reset). */
const ACTION_URL = `${window.location.origin}/reset-password`;

const googleProvider = new GoogleAuthProvider();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  // Firebase mutates the User object in place (e.g. after reload()); bump this to
  // force consumers to re-read fields like `emailVerified` without losing the instance.
  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  // Persist + react to auth state across reloads and tabs.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (u) {
        u.getIdTokenResult().then((r) => setIsAdmin(r.claims.platformAdmin === true)).catch(() => setIsAdmin(false));
      } else {
        setIsAdmin(false);
      }
    });
    return unsub;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    await withAuthError(async () => {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await fbSendEmailVerification(cred.user, { url: ACTION_URL });
      reportReferralConversion('signup').catch(() => undefined);
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await withAuthError(() => signInWithEmailAndPassword(auth, email, password));
  }, []);

  const signInWithGoogle = useCallback(async () => {
    await withAuthError(async () => {
      await signInWithPopup(auth, googleProvider);
    });
  }, []);

  const signOut = useCallback(async () => {
    await withAuthError(() => fbSignOut(auth));
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    await withAuthError(() => sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/login` }));
  }, []);

  const confirmPasswordReset = useCallback(async (oobCode: string, newPassword: string) => {
    await withAuthError(() => fbConfirmPasswordReset(auth, oobCode, newPassword));
  }, []);

  const sendEmailVerification = useCallback(async () => {
    if (!auth.currentUser) throw new Error('You must be signed in to verify your email.');
    await withAuthError(() => fbSendEmailVerification(auth.currentUser!, { url: ACTION_URL }));
  }, []);

  const reloadUser = useCallback(async () => {
    if (!auth.currentUser) return;
    await withAuthError(() => auth.currentUser!.reload());
    // reload() mutates currentUser in place — re-render so consumers see fresh fields.
    setUser(auth.currentUser);
    forceRender();
  }, []);

  const reauthenticate = useCallback(async (currentPassword: string) => {
    const u = auth.currentUser;
    if (!u?.email) throw new Error('You must be signed in to do this.');
    const credential = EmailAuthProvider.credential(u.email, currentPassword);
    await withAuthError(() => reauthenticateWithCredential(u, credential));
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!auth.currentUser) throw new Error('You must be signed in to change your password.');
    await withAuthError(() => fbUpdatePassword(auth.currentUser!, newPassword));
  }, []);

  const deleteAccount = useCallback(async (currentPassword: string) => {
    const u = auth.currentUser;
    if (!u) throw new Error('You must be signed in to delete your account.');
    await reauthenticate(currentPassword);
    await withAuthError(() => deleteUser(u));
  }, [reauthenticate]);

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    confirmPasswordReset,
    sendEmailVerification,
    reloadUser,
    updatePassword,
    reauthenticate,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
